import * as APIRoutes from '@mm/api-routes';
import { buildQueryString } from '@mm/utils';
import { ExtendedAdConfigModel } from '../model/AdConfigModel';
import {
  AdsVacantCouponModel,
  AttachCouponModel,
} from '../model/AdCouponModel';
import { AdModel } from '../model/AdModel';
import AuthenticatedController from './AuthenticatedController';

type AvailableAdsOptions = {
  botId?: number;
  accountId?: string;
  campaignId?: string;
  next?: string;
  limit?: number;
};

export type SearchEntityType = {
  type: 'promo_blaster' | 'ctm_ad' | 'synced_ad';
  id: number;
};

export default class AdConfigController extends AuthenticatedController {
  static search(
    query: string,
    specialCategory: boolean,
    entity?: SearchEntityType,
  ) {
    return super.get(
      APIRoutes.AD_CONFIGS_SEARCH(
        query,
        specialCategory,
        JSON.stringify(entity),
      ),
    );
  }

  static async availableAds(
    options: AvailableAdsOptions,
  ): Promise<{ data: AdModel[]; next: string | null; error?: string }> {
    const { accountId, campaignId, next, limit, botId } = options;
    const queryString = buildQueryString({
      ad_account_id: accountId,
      ad_campaign_id: campaignId,
      bot_id: botId,
      next,
      limit,
    });
    return super.get(APIRoutes.FULL_ADS(queryString));
  }

  static getAll(botId: number) {
    return super.get(APIRoutes.AD_CONFIGS(botId));
  }

  static getById(botId: number, adConfigId: number) {
    return super.get(APIRoutes.AD_CONFIG(botId, adConfigId));
  }

  static prepareReplacementPage(
    botId: number,
    entityId: number,
    pageId?: number,
  ) {
    return super.post(APIRoutes.AD_CONFIGS_REPLACEMENT_PAGE(botId, entityId), {
      pageId,
    });
  }

  static postAdConfig(botId: number, entity: Partial<ExtendedAdConfigModel>) {
    return super.post(APIRoutes.AD_CONFIGS(botId), entity);
  }

  static putAdConfig(botId: number, entity: Partial<ExtendedAdConfigModel>) {
    return super.put(APIRoutes.AD_CONFIG(botId, entity.id as number), entity);
  }

  static publish(botId: number, adConfigId: number) {
    return super.post(APIRoutes.PUBLISH_AD_CONFIG(botId, adConfigId), {});
  }

  static getTemplates(botId: number) {
    return super.get(APIRoutes.MESSENGER_AD_TEMPLATES(botId));
  }

  static deleteAdConfig(botId: number, adConfigId: number) {
    return super.delete(APIRoutes.AD_CONFIG(botId, adConfigId));
  }

  static getVacantAdsCoupons(
    facebookPageId?: number,
  ): Promise<AdsVacantCouponModel> {
    return super.get(APIRoutes.GET_FB_VACANT_ADS_COUPONS, {
      facebook_page_id: facebookPageId,
    });
  }

  static attachAdCoupon(
    governmental: boolean,
    facebookPageId: number,
  ): Promise<AttachCouponModel> {
    return super.post(APIRoutes.FB_ATTACH_VACANT_AD_COUPON, {
      governmental,
      facebookPageId,
    });
  }
}
